import { getAuthHeaders } from "./authSession";

type GoogleCalTokenRow = {
  accessToken: string;
  refreshToken?: string;
  tokenType?: string; // usually "Bearer"
  scope?: string;
  expiresAt?: number | null; // epoch ms
  savedAt?: number; // epoch ms
};

export class GoogleCalTokenApi {
  constructor() {}

  private async post<T>(payload: any): Promise<T> {
    const res = await fetch(process.env.REACT_APP_API_ENDPOINT!, {
      method: "POST",
      headers: { "content-type": "application/json", ...getAuthHeaders() },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `Request failed (${res.status})`);
    }

    return (await res.json()) as T;
  }

  saveGoogleCalToken(payload: {
    accessToken: string;
    expiresIn?: number; // seconds
    scope?: string;
    tokenType?: string;
  }) {
    return this.post<{ ok: true }>({
      action: "saveGoogleCalToken",
      ...payload,
    });
  }

  getGoogleCalToken() {
    return this.post<GoogleCalTokenRow | null>({
      action: "getGoogleCalToken",
    });
  }

  exchangeAuthCode(payload: {
    code: string;
    redirectUri: string;
    clientId?: string;
    clientSecret?: string;
  }) {
    return this.post<{ ok: true; accessToken: string; expiresAt: number }>({
      action: "exchangeAuthCode",
      ...payload,
    });
  }

  refreshAccessToken() {
    return this.post<{ ok: true; accessToken: string; expiresAt: number }>({
      action: "refreshAccessToken",
    });
  }

  /**
   * Gets a valid access token, automatically refreshing if expired.
   * Returns null if no token exists or refresh fails.
   */
  async getValidAccessToken(): Promise<string | null> {
    try {
      const tokenData = await this.getGoogleCalToken();

      if (!tokenData?.accessToken) {
        return null;
      }

      // Check if token is expired or will expire in the next 5 minutes
      const now = Date.now();
      const expiresAt = tokenData.expiresAt ?? 0;
      const bufferTime = 5 * 60 * 1000; // 5 minutes in ms

      if (expiresAt > now + bufferTime) {
        // Token is still valid
        return tokenData.accessToken;
      }

      // Token is expired or expiring soon, try to refresh
      if (!tokenData.refreshToken) {
        console.warn("Token expired and no refresh token available");
        return null;
      }

      const refreshResult = await this.refreshAccessToken();
      return refreshResult.accessToken;
    } catch (e) {
      console.error("Failed to get valid access token:", e);
      return null;
    }
  }
}
